import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/components/ui/use-toast';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '@/lib/firebase';
import { useAppConfig } from '@/contexts/AppConfigContext';
import { useTranslation } from 'react-i18next';

const functions = getFunctions(app, 'us-central1');

// ── Web Speech API Type Declarations ──────────────────────────────────────
interface SpeechRecognitionEvent extends Event {
  results: {
    length: number;
    [index: number]: {
      isFinal?: boolean;
      [index: number]: {
        transcript: string;
      };
    };
  };
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message?: string;
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognitionInstance;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

// ── Types for OpenAI Cloud Function ───────────────────────────────────────
interface VoiceIntentRequest {
  transcript?: string;
  audioBase64?: string;
  audioMimeType?: string;
  currentPath: string;
}

interface VoiceIntentResponse {
  action:
    | 'NAVIGATE_AND_PREFILL'
    | 'NAVIGATE_ONLY'
    | 'OPEN_CUSTOMER'
    | 'EDIT_CUSTOMER'
    | 'OPEN_EVENT'
    | 'EDIT_EVENT'
    | 'TOGGLE_DARK_MODE'
    | 'CREATE_PACKAGE'
    | 'EDIT_PACKAGE'
    | 'CREATE_PAYMENT'
    | 'GENERATE_EMAIL'
    | 'ANSWER_QUERY'
    | 'UNKNOWN';
  targetRoute: string;
  prefillData?: Record<string, unknown>;
  feedbackMessage: string;
}

// ── Normalizador Fonético y Spanglish ─────────────────────────────────────
function cleanVoicePhonetics(input: string): string {
  let text = input.trim();
  // Corregir deformaciones fonéticas habituales de Web Speech API (Spanglish / acentos)
  text = text.replace(/\b(toco|toca|tocó)\s+(dar\s*mo|dar\s*mot|dark\s*mod)\b/gi, 'toggle dark mode');
  text = text.replace(/\b(modo\s+obscuro|pantalla\s+oscura)\b/gi, 'modo oscuro');
  text = text.replace(/\b(peip\s*lain|pay\s*plain|peipláin|payplain|pipelayn|tubería)\b/gi, 'pipeline');
  text = text.replace(/\b(dasbor|dasboard)\b/gi, 'dashboard');
  text = text.replace(/\b(setins|ceitings)\b/gi, 'settings');
  return text;
}

// ── Helper para determinar formato de audio soportado por el navegador ───
function getSupportedAudioMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/wav',
  ];
  for (const t of types) {
    if (MediaRecorder.isTypeSupported(t)) {
      return t;
    }
  }
  return '';
}

// ── Context Interface ─────────────────────────────────────────────────────
interface VoiceCopilotContextType {
  isListening: boolean;
  isProcessing: boolean;
  isSpeaking: boolean;
  isMuted: boolean;
  isSupported: boolean;
  transcript: string;
  lastCommand: string;
  pendingPrefill: Record<string, unknown> | null;
  startListening: () => void;
  stopListening: () => void;
  toggleListening: () => void;
  toggleMute: () => void;
  stopSpeaking: () => void;
  clearPendingPrefill: () => void;
}

const VoiceCopilotContext = createContext<VoiceCopilotContextType | undefined>(undefined);

interface VoiceCopilotProviderProps {
  children: ReactNode;
}

export const VoiceCopilotProvider: React.FC<VoiceCopilotProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const { toggleDarkMode } = useAppConfig();
  const { i18n } = useTranslation();
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState<boolean>(() => {
    try {
      return localStorage.getItem('bassline_voice_muted') === 'true';
    } catch {
      return false;
    }
  });
  const [transcript, setTranscript] = useState('');
  const [lastCommand, setLastCommand] = useState('');
  const [pendingPrefill, setPendingPrefill] = useState<Record<string, unknown> | null>(null);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const latestTranscriptRef = useRef<string>('');
  const stopTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isSupported =
    typeof window !== 'undefined' &&
    (!!(window.SpeechRecognition || window.webkitSpeechRecognition) ||
      !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia));

  const clearPendingPrefill = useCallback(() => {
    setPendingPrefill(null);
  }, []);

  useEffect(() => {
    return () => {
      if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current);
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  // ── Fallback Intent Matching (en caso de offline o servidor inaccesible) ──
  const fallbackProcessVoiceCommand = useCallback(
    (rawTranscript: string) => {
      const normalized = cleanVoicePhonetics(rawTranscript);
      const text = normalized
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[¿?¡!.,;]/g, '')
        .trim();

      const routes: Array<{ keywords: string[]; route: string; label: string }> = [
        {
          keywords: [
            'nuevo cliente',
            'crear cliente',
            'agregar cliente',
            'registrar cliente',
            'anadir cliente',
            'new customer',
            'create customer',
            'add customer',
            'register customer',
            'new client',
            'create client',
            'add client',
          ],
          route: '/customers/new',
          label: 'Nuevo Cliente / New Customer',
        },
        {
          keywords: [
            'clientes',
            'lista de clientes',
            'ir a clientes',
            'ver clientes',
            'mis clientes',
            'directorio',
            'customers',
            'customer list',
            'go to customers',
            'view customers',
            'my customers',
            'clients',
          ],
          route: '/customers',
          label: 'Clientes / Customers',
        },
        {
          keywords: [
            'nuevo evento',
            'crear evento',
            'agendar evento',
            'nueva cotizacion',
            'crear cotizacion',
            'nueva fecha',
            'agendar fecha',
            'new event',
            'create event',
            'schedule event',
            'new quote',
            'new quotation',
            'create quote',
            'new booking',
            'book event',
          ],
          route: '/events/new',
          label: 'Nuevo Evento / New Event',
        },
        {
          keywords: [
            'eventos',
            'lista de eventos',
            'ir a eventos',
            'ver eventos',
            'mis eventos',
            'calendario',
            'agenda',
            'events',
            'event list',
            'go to events',
            'view events',
            'my events',
            'calendar',
            'schedule',
          ],
          route: '/events',
          label: 'Eventos / Events',
        },
        {
          keywords: [
            'ventas',
            'ir a ventas',
            'pipeline',
            'ver ventas',
            'embudo de ventas',
            'embudo',
            'sales',
            'go to sales',
            'view sales',
            'pipeline de ventas',
            'sales pipeline',
            'leads',
            'deals',
          ],
          route: '/sales',
          label: 'Ventas / Sales Pipeline',
        },
        {
          keywords: [
            'nuevo pago',
            'crear pago',
            'registrar pago',
            'cobrar',
            'recibir pago',
            'new payment',
            'create payment',
            'register payment',
            'record payment',
            'charge',
          ],
          route: '/payments/new',
          label: 'Nuevo Pago / New Payment',
        },
        {
          keywords: [
            'pagos',
            'lista de pagos',
            'ir a pagos',
            'ver pagos',
            'cobros',
            'finanzas',
            'ingresos',
            'facturas',
            'payments',
            'payment list',
            'go to payments',
            'view payments',
            'finances',
            'billing',
            'revenue',
          ],
          route: '/payments',
          label: 'Pagos / Payments',
        },
        {
          keywords: [
            'inventario',
            'paquetes',
            'ir a inventario',
            'ver inventario',
            'equipos',
            'servicios',
            'luces',
            'audio',
            'sonido',
            'inventory',
            'packages',
            'go to inventory',
            'view inventory',
            'equipment',
            'gear',
            'services',
          ],
          route: '/inventory',
          label: 'Inventario / Inventory',
        },
        {
          keywords: [
            'configuracion',
            'ajustes',
            'perfil',
            'logo',
            'datos del artista',
            'mi cuenta',
            'settings',
            'configuration',
            'profile',
            'artist profile',
            'account',
          ],
          route: '/settings',
          label: 'Configuración / Settings',
        },
        {
          keywords: [
            'admin',
            'administracion',
            'panel admin',
            'kpis',
            'metricas',
            'salud',
            'administration',
            'admin panel',
            'metrics',
            'health',
          ],
          route: '/admin',
          label: 'Admin Panel',
        },
        {
          keywords: [
            'modo oscuro',
            'modo claro',
            'dark mode',
            'cambiar tema',
            'apagar modo oscuro',
            'encender modo oscuro',
            'light mode',
            'toggle dark mode',
            'toggle theme',
            'switch theme',
          ],
          route: 'TOGGLE_DARK_MODE',
          label: 'Modo de Visualización / Theme',
        },
        {
          keywords: [
            'inicio',
            'dashboard',
            'resumen',
            'home',
            'pantalla principal',
            'principal',
            'main screen',
            'main dashboard',
          ],
          route: '/',
          label: 'Dashboard',
        },
      ];

      for (const item of routes) {
        if (item.keywords.some((kw) => text.includes(kw))) {
          if (item.route === 'TOGGLE_DARK_MODE') {
            toggleDarkMode();
            toast({
              title: '✨ BASS Line AI',
              description: 'Modo de visualización actualizado.',
            });
            return;
          }
          navigate(item.route);
          toast({
            title: '✨ BASS Line AI',
            description: `Navegando a ${item.label}...`,
          });
          return;
        }
      }

      toast({
        title: '✨ BASS Line AI',
        description: `No reconocí el comando: "${rawTranscript}". Puedes decir "Ir a clientes", "Nuevo evento", "Ver pagos", "Pipeline", etc.`,
      });
    },
    [navigate, toggleDarkMode]
  );

  const stopSpeaking = useCallback(() => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('bassline_voice_muted', String(next));
      } catch {
        // ignore
      }
      if (next && typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
      }
      return next;
    });
  }, []);

  const speakFeedback = useCallback(
    (text: string) => {
      if (isMuted) return;
      if (typeof window === 'undefined' || !window.speechSynthesis) return;

      try {
        window.speechSynthesis.cancel();
        const spokenText = text.replace(/[*_#`~[\]]/g, '').trim();
        if (!spokenText) return;

        const utterance = new SpeechSynthesisUtterance(spokenText);
        utterance.rate = 1.05;
        utterance.pitch = 1.0;

        const isEnglish =
          /^(this|you|there|opening|navigating|here|current|how|what)/i.test(spokenText) ||
          (i18n.language && i18n.language.startsWith('en'));

        utterance.lang = isEnglish ? 'en-US' : 'es-419';

        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          const matchingVoice =
            voices.find((v) =>
              isEnglish
                ? v.lang.startsWith('en') &&
                  (v.name.includes('Natural') ||
                    v.name.includes('Samantha') ||
                    v.name.includes('Google') ||
                    v.name.includes('Daniel'))
                : v.lang.startsWith('es') &&
                  (v.name.includes('Natural') ||
                    v.name.includes('Monica') ||
                    v.name.includes('Google') ||
                    v.name.includes('Paulina'))
            ) ||
            voices.find((v) =>
              isEnglish ? v.lang.startsWith('en') : v.lang.startsWith('es')
            );

          if (matchingVoice) {
            utterance.voice = matchingVoice;
          }
        }

        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);

        window.speechSynthesis.speak(utterance);
      } catch (e) {
        console.warn('Speech synthesis error:', e);
        setIsSpeaking(false);
      }
    },
    [isMuted, i18n.language]
  );

  // ── Process Voice Command with OpenAI LLM / Whisper ────────────────────
  const processVoiceCommand = useCallback(
    async (rawTranscript?: string, audioBase64?: string, audioMimeType?: string) => {
      const cleanTranscript = rawTranscript ? cleanVoicePhonetics(rawTranscript).trim() : '';
      if (!cleanTranscript && !audioBase64) return;

      if (cleanTranscript) {
        setLastCommand(cleanTranscript);
      }
      setIsProcessing(true);

      toast({
        title: '✨ BASS Line AI',
        description: audioBase64
          ? 'Procesando voz con OpenAI Whisper...'
          : `Analizando orden: "${cleanTranscript}"...`,
      });

      try {
        const callable = httpsCallable<VoiceIntentRequest, VoiceIntentResponse>(
          functions,
          'processVoiceIntent'
        );
        const result = await callable({
          transcript: cleanTranscript || undefined,
          audioBase64: audioBase64 || undefined,
          audioMimeType: audioMimeType || undefined,
          currentPath: window.location.pathname,
        });

        const { action, targetRoute, prefillData, feedbackMessage } = result.data;

        // Reproducir confirmación o respuesta auditiva por voz
        if (feedbackMessage) {
          speakFeedback(feedbackMessage);
        }

        if (action === 'TOGGLE_DARK_MODE') {
          toggleDarkMode();
          toast({
            title: '✨ BASS Line AI',
            description: feedbackMessage || 'Modo visual cambiado exitosamente.',
          });
          setIsProcessing(false);
          return;
        }

        if (action === 'ANSWER_QUERY') {
          toast({
            title: '💬 BASS Line AI',
            description: feedbackMessage,
          });
          if (targetRoute && targetRoute !== window.location.pathname) {
            navigate(targetRoute);
          }
          setIsProcessing(false);
          return;
        }

        if (prefillData && Object.keys(prefillData).length > 0) {
          setPendingPrefill(prefillData);
        } else {
          setPendingPrefill(null);
        }

        toast({
          title: '✨ BASS Line AI',
          description: feedbackMessage,
        });

        if (targetRoute && targetRoute !== window.location.pathname) {
          navigate(targetRoute);
        } else if (action === 'NAVIGATE_ONLY' && targetRoute) {
          navigate(targetRoute);
        }
      } catch (error) {
        console.warn('OpenAI Cloud Function fallback to local matcher:', error);
        if (cleanTranscript) {
          fallbackProcessVoiceCommand(cleanTranscript);
        } else {
          toast({
            title: '✨ BASS Line AI',
            description: 'No pudimos procesar el comando de voz. Por favor intenta de nuevo.',
            variant: 'destructive',
          });
        }
      } finally {
        setIsProcessing(false);
      }
    },
    [navigate, fallbackProcessVoiceCommand, toggleDarkMode, speakFeedback]
  );

  // ── Start Listening (MediaRecorder + SpeechRecognition preview) ─────────
  const startListening = useCallback(async () => {
    if (!isSupported) {
      toast({
        title: '✨ BASS Line AI',
        description: 'Tu navegador no soporta el acceso al micrófono.',
        variant: 'destructive',
      });
      return;
    }

    if (isListening) return;

    setTranscript('');
    latestTranscriptRef.current = '';
    audioChunksRef.current = [];

    // 1. Iniciar MediaRecorder para grabación de audio real (para Whisper universal)
    if (typeof navigator !== 'undefined' && navigator.mediaDevices?.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaStreamRef.current = stream;
        const mimeType = getSupportedAudioMimeType();
        const mr = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream);

        mr.ondataavailable = (event: BlobEvent) => {
          if (event.data && event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mr.onstop = () => {
          const recordedChunks = [...audioChunksRef.current];
          audioChunksRef.current = [];

          // Apagar tracks de audio para que no quede el indicador rojo activo en el navegador
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach((track) => track.stop());
            mediaStreamRef.current = null;
          }

          if (recordedChunks.length > 0) {
            const audioBlob = new Blob(recordedChunks, {
              type: mr.mimeType || 'audio/webm',
            });
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64Data = (reader.result as string)?.split(',')[1];
              void processVoiceCommand(
                latestTranscriptRef.current,
                base64Data,
                audioBlob.type
              );
            };
            reader.readAsDataURL(audioBlob);
          } else if (latestTranscriptRef.current) {
            void processVoiceCommand(latestTranscriptRef.current);
          }
        };

        mr.start(250);
        mediaRecorderRef.current = mr;
      } catch (micErr) {
        console.warn('MediaRecorder error or mic permission denied:', micErr);
      }
    }

    // 2. Iniciar Web Speech API en paralelo para mostrar previsualización de texto en vivo en la UI
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang =
        i18n.language && i18n.language.startsWith('en') ? 'en-US' : 'es-419';

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let combinedText = '';
        for (let i = 0; i < event.results.length; i++) {
          const text = event.results[i][0].transcript.trim();
          if (!text) continue;
          if (!combinedText) {
            combinedText = text;
          } else if (text.toLowerCase().startsWith(combinedText.toLowerCase())) {
            combinedText = text;
          } else if (combinedText.toLowerCase().includes(text.toLowerCase())) {
            continue;
          } else {
            combinedText += ' ' + text;
          }
        }
        const trimmed = combinedText.trim();
        latestTranscriptRef.current = trimmed;
        setTranscript(trimmed);
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        if (event.error !== 'no-speech' && event.error !== 'aborted') {
          console.warn('SpeechRecognition preview error:', event.error);
        }
      };

      recognition.onend = () => {
        // Si MediaRecorder no estuvo disponible, procesar el texto capturado por SpeechRecognition
        if (!mediaRecorderRef.current && latestTranscriptRef.current) {
          void processVoiceCommand(latestTranscriptRef.current);
        }
      };

      recognitionRef.current = recognition;

      try {
        recognition.start();
      } catch (e) {
        console.warn('Failed to start SpeechRecognition preview:', e);
      }
    }

    setIsListening(true);
  }, [isSupported, isListening, processVoiceCommand, i18n.language]);

  // ── Stop Listening ──────────────────────────────────────────────────────
  const stopListening = useCallback(() => {
    if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current);

    stopTimeoutRef.current = setTimeout(() => {
      // 1. Detener MediaRecorder para emitir onstop y procesar con Whisper
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive'
      ) {
        try {
          mediaRecorderRef.current.stop();
        } catch (e) {
          console.error('Error stopping MediaRecorder:', e);
        }
      }

      // 2. Detener Web Speech API preview
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          console.error('Error stopping SpeechRecognition:', e);
        }
      }
    }, 150);

    setIsListening(false);
  }, []);

  // ── Toggle Listening ────────────────────────────────────────────────────
  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  // ── Keyboard Shortcut: Cmd+K / Ctrl+K ───────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        toggleListening();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleListening]);

  return (
    <VoiceCopilotContext.Provider
      value={{
        isListening,
        isProcessing,
        isSpeaking,
        isMuted,
        isSupported,
        transcript,
        lastCommand,
        pendingPrefill,
        startListening,
        stopListening,
        toggleListening,
        toggleMute,
        stopSpeaking,
        clearPendingPrefill,
      }}
    >
      {children}
    </VoiceCopilotContext.Provider>
  );
};

export const useVoiceCopilot = (): VoiceCopilotContextType => {
  const context = useContext(VoiceCopilotContext);
  if (!context) {
    throw new Error('useVoiceCopilot must be used within a VoiceCopilotProvider');
  }
  return context;
};
